// @flow

import React from 'react';
import { observer } from 'mobx-react';
import moment from 'moment';
import { css, after } from 'glamor';
import { Row, Col, Image } from 'react-bootstrap';

import Flag from '~/components/global/Flag';
import Genre from '~/components/content/game/Genre';
import Stars from '~/components/global/Stars';
import Score from '~/components/global/Score';
import type { Game as GameType, Review as ReviewType, Dates } from '~/types';

const gameRule = css(
    {
        padding: '25px',
        paddingLeft: 0,
        width: '100%',
        color: '#fff',
        position: 'relative',
        zIndex: 1,
        backgroundSize: 'cover',

        '@media(max-width: 768px)': {
            paddingLeft: '25px',
        },

        '& .title': {
            fontSize: '1.9em',
            fontWeight: 900,
            textTransform: 'uppercase',
            color: 'rgb(240, 53, 15)',
        },

        '& .description': {
            minHeight: '80px',
            marginTop: '20px',
            height: '80px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'justify',
        },

        '& .link': {
            textDecoration: 'none',
        },
    },
    after({
        zIndex: -1,
        content: '" "',
        position: 'absolute',
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,.8)',
    })
);

const flagRule = css({
    marginRight: '5px',
});

const dateRule = css({
    whiteSpace: 'nowrap',
});

const rowSpaceSmall = css({
    paddingTop: '10px',
});

const imageRule = css({
    margin: '0 auto',
    paddingLeft: '20px',
    paddingTop: '10px',
});

const japaneseRule = css({
    marginLeft: '15px',

    '@media(max-width: 768px)': {
        marginLeft: 0,
    },
});

const detailRule = css({
    fontWeight: 700,
    whiteSpace: 'nowrap',
});

const scoreRule = css({
    marginTop: '20px',
});

const genreListRule = css({
    paddingLeft: 0,
    listStyle: 'none',
});

const genreRule = css({
    display: 'inline-block',
    fontWeight: 700,
    marginLeft: '10px',
});

const labelRule = css({
    fontWeight: 500,
});

@observer
export default class GameDetails extends React.Component {
    props: {
        game: GameType,
        review: ReviewType,
    };

    renderDates(dates: Dates) {
        const result = {};

        Object.keys(dates).forEach(key => {
            if (dates[key]) {
                result[key] = (
                    <span { ...dateRule }>
                        <span { ...flagRule }>
                            <Flag country={ key } />
                        </span>
                        <span>
                            {moment(new Date(dates[key])).format('DD MMM YYYY')}
                        </span>
                    </span>
                );
            }
        });

        return result;
    }

    render() {
        const { game, review } = this.props;

        if (!game) {
            return null;
        }

        const ReleaseDates = this.renderDates(game.releaseDates || {});
        const backStyle = game.backgroundImage ?
        {
            backgroundImage: `url('${game.backgroundImage.secure_url}'`,
        } :
            null;
        const imageUrl = (() => {
            const original = game.image && game.image.secure_url;
            const parts = original.split('/');
            parts[parts.length - 2] = 'w_400,h_400,c_fit';

            return parts.join('/');
        })();
        const AESReleaseDate = moment(new Date(game.releaseDates.aes)).format(
            'DD MMM YYYY'
        );
        const MVSReleaseDate = moment(new Date(game.releaseDates.mvs)).format(
            'DD MMM YYYY'
        );

        return (
            <div { ...gameRule } style={ backStyle }>
                <Row>
                    <Col md={ 4 }>
                        <Image
                            src={ imageUrl }
                            alt={ game.name }
                            title={ game.name }
                            responsive
                            { ...imageRule }
                        />
                    </Col>

                    <Col md={ 8 }>
                        <h4 className="title">
                            {game.name}
                        </h4>
                        <h4 className="title">
                            <span { ...japaneseRule }>
                                {game.japanese}
                            </span>
                        </h4>

                        <Row { ...rowSpaceSmall }>
                            <Col md={ 4 } { ...detailRule }>
                                {review.platform.name}
                            </Col>
                            <Col md={ 4 }>
                                <span>
                                    <ul { ...genreListRule }>
                                        {game.genres.map(genre => (
                                            <li key={ genre.id } { ...genreRule }>
                                                <Genre genre={ genre } />
                                            </li>
                                        ))}
                                    </ul>
                                </span>
                            </Col>
                            <Col md={ 4 }>
                                <Row>
                                    <Col md={ 4 } xs={ 3 }>
                                        <span { ...labelRule }>
                                            {'Difficulty:'}
                                        </span>
                                    </Col>
                                    <Col md={ 8 } xs={ 9 }>
                                        <Stars
                                            rating={ review.difficulty }
                                            maxRating={ 6 }
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={ 8 }>
                                <Row>
                                    <Col md={ 6 }>
                                        <Row>
                                            <Col md={ 6 } xs={ 3 }>
                                                <span { ...labelRule }>
                                                    {'Publisher:'}
                                                </span>
                                            </Col>
                                            <Col md={ 6 } xs={ 9 }>
                                                <span { ...detailRule }>
                                                    {game.publisher ?
                                                        game.publisher.name :
                                                        'N/A'}
                                                </span>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col md={ 6 } { ...detailRule }>
                                        {game.players}
                                    </Col>
                                </Row>
                                <Row { ...rowSpaceSmall }>
                                    <Col md={ 6 }>
                                        <Row>
                                            <Col md={ 6 } xs={ 3 }>
                                                <span { ...labelRule }>
                                                    {'Developer:'}
                                                </span>
                                            </Col>
                                            <Col md={ 6 } xs={ 9 }>
                                                <span { ...detailRule }>
                                                    {game.developer ?
                                                        game.developer.name :
                                                        'N/A'}
                                                </span>
                                            </Col>
                                        </Row>
                                    </Col>
                                    <Col md={ 6 } { ...detailRule }>
                                        {game.saving}
                                    </Col>
                                </Row>
                                {game.releaseDates.jp ||
                                    game.releaseDates.eu ||
                                    game.releaseDates.eu ?
                                    <Row { ...rowSpaceSmall }>
                                          <Col md={ 3 } { ...labelRule }>
                                              {'Release date:'}
                                          </Col>
                                          <Col md={ 3 } { ...detailRule }>
                                              {ReleaseDates.jp}
                                          </Col>
                                          <Col md={ 3 } { ...detailRule }>
                                              {ReleaseDates.us}
                                          </Col>
                                          <Col md={ 3 } { ...detailRule }>
                                              {ReleaseDates.eu}
                                          </Col>
                                      </Row> :
                                    null}
                                {game.releaseDates.aes || game.releaseDates.mvs ?
                                    <Row { ...rowSpaceSmall }>
                                          <Col md={ 3 } { ...labelRule }>
                                              {'Release date:'}
                                          </Col>
                                          {game.releaseDates.aes ?
                                              <Col md={ 3 } { ...detailRule }>
                                                    {`AES ${AESReleaseDate}`}
                                                </Col> :
                                              null}
                                          {game.releaseDates.mvs ?
                                              <Col md={ 3 } { ...detailRule }>
                                                    {`MVS ${MVSReleaseDate}`}
                                                </Col> :
                                              null}
                                      </Row> :
                                    null}
                            </Col>
                            <Col md={ 4 }>
                                <span { ...labelRule }>
                                    {'Final score:'}
                                </span>
                                <div { ...scoreRule }>
                                    <Score
                                        score={ review.objectiveScore.score }
                                        maxScore={ 100 }
                                    />
                                </div>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={ 12 }>
                                <p className="description">
                                    {game.content.brief.md}
                                </p>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/content/game/GameDetails/index.js